import Raven from 'raven-js';
import cookie from 'react-cookie';
import { digestCookies } from '@letsdothis/quarantine';
import { PageExperiments } from '@letsdothis/experiments';

import utm from '../../utils/utm';
import { getLocationInfoForCurrentUser } from '../../utils/ipstack';
import {
  getAllSegmentTrackingEvents,
  getAllPageEvents,
} from '../../utils/trackingConstants';
import { REFERRER_COOKIE_NAME } from './auth';
import { postJSON } from './helpers';

// Configuration for Tracking API
// (visible-for-testing)
export const TRACKING_ROOT = '/track/';

/* === Init Tracking Info === */

/**
 * Following frequent referrer loss/reset/confusion, we store the referrer in a cookie.
 * This is used in subsequent tracking and page events, available to new tabs and post-login.
 *
 * The referrer cookie is only read in this file. It is set in ./auth/getLock#getLock.
 */
const trackingReferrer = cookie.load(REFERRER_COOKIE_NAME);
if (trackingReferrer) {
  // Google Analytics needs to be set explicity.
  ga('set', 'referrer', trackingReferrer);
}

// Google Analytics needs e-commerce tracking specifically enabled
if (__CLIENT__ && typeof ga !== 'undefined') {
  ga('require', 'ecommerce');
}

const anonymousId = cookie.load('ajs_anonymous_id');
if (__CLIENT__ && window.analytics && anonymousId) {
  analytics.ready(() => {
    // Anonymous Id cookie wrapped in quotes. We strip them before we set it.
    // eslint-disable-next-line no-unused-expressions
    analytics?.user?.().anonymousId?.(anonymousId.replace(/^"+|"+$/g, ''));
  });
}

/**
 * To ensure that campaign information is persistent, store the object at load for 30mins,
 * this is then passed onto track and page calls to override Segment defaults.
 */
export const CAMPAIGN_COOKIE_NAME = 'campaign';
if (__CLIENT__ && !cookie.load(CAMPAIGN_COOKIE_NAME)) {
  const params = utm(window.location.search);
  if (Object.keys(params).length) {
    cookie.save(CAMPAIGN_COOKIE_NAME, params, {
      domain: window.location.hostname,
      expires: new Date(+new Date() + 120 * 60 * 1000), // 120mins from now
      path: '/', // Required to be used site-wide
      secure: !__DEVELOPMENT__,
    });
  }
}

// Load google analytics client id https://segment.com/docs/destinations/google-analytics/#combining-server-side-and-client-side-events
const googleAnalyticsClientId = cookie.load('_ga');

let userLocationInfo;
if (__CLIENT__) {
  // Set location info once request returns.
  getLocationInfoForCurrentUser().then((locInfo) => {
    // Do not allow properties of userLocationInfo to be changed once it's set.
    userLocationInfo = Object.freeze(locInfo);
  });
}

/* === End Init Tracking Info === */

/**
 * Tracking Service methods
 *
 * These functions wrap other services, e.g. our tracking service, or Segment's JS library.
 * The aim here is to provide a simple interface for tracking calls which don't required
 * knowledge the third-parties, or which calls are required to be performed client- or server-side.
 * Additionally, they reuse common properties, e.g. user identification.
 *
 * TODO: This service should probably aligned with the others (API, auth), which are called directly
 * by components, where these are largely called by the tracking utility functions.
 */
const trackingService = {};

const getPageExperiments = () => {
  if (!__CLIENT__) return { serverside: true };
  const url = window.location.href;
  return PageExperiments.getExperimentsFromUrl(url);
};

/**
 * Returns additional information relativate to an event sent to segment.
 *
 * Segment inherintly captures certain information about the browser for every call (see
 * getClientContext() below for more information). This function will gather all additional
 * useful context that should also be tracked, but isn't automatically picked up by Segment.
 */
const getAdditionalContext = () => ({
  campaign: cookie.load(CAMPAIGN_COOKIE_NAME),
  location: userLocationInfo,
  // TODO(ryan@): This is kind of a hacky way to do this, we should really be reading from the
  //              store. Consider investigating ways to universally get access to the store here
  //              without needing to pass store into every tracking call.
  experiments: digestCookies(cookie.select()),
  pageExperiments: getPageExperiments(),
});

/**
 * Capture browser/page/location properties of current window and add additional global properties.
 *
 * Calls to segment (i.e. analytics.track(...), analytics.page(...), etc) inherently pass
 * information about the browser. However, when we make tracking calls to the Let's Do This server
 * (such as via trackingService.trackDataEvent(...)) we do not have access to this information.
 * This function servers to mimic the automatically captured browser information from segment
 * so that it can be passed to the server.
 *
 * (visible-for-testing)
 */
export const getClientContext = () =>
  __CLIENT__ && {
    campaign: cookie.load(CAMPAIGN_COOKIE_NAME),
    page: {
      path: window.location.pathname,
      referrer: trackingReferrer || window.document.referrer,
      search: window.location.search,
      title: window.document.title,
      url: window.location.href,
    },
    userAgent: window.navigator.userAgent,
    ...(googleAnalyticsClientId && {
      'Google Analytics': {
        clientId: googleAnalyticsClientId.replace('GA1.2.', ''),
      },
    }),
    ...getAdditionalContext(),
  };

/**
 * Track user alias with Segment Analytics alias method.
 */
trackingService.aliasUser = (profile) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      analytics.alias(
        profile.primaryIdentityId,
        analytics?.user?.().anonymousId?.(),
      );
    });
  }
};

/**
 * Track user with Segment Analytics identify method.
 */
trackingService.identifyUser = (profile) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      analytics.identify(profile.primaryIdentityId);
    });
  }
};

/**
 * Track simple event and properties with Segment Analytics track method.
 */
trackingService.trackEvent = (event, properties, delay = 500) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      const existingEvents = getAllSegmentTrackingEvents();
      if (!existingEvents.includes(event)) {
        Raven.captureMessage(
          `Unknown tracking event "${event}" being submitted. Please add your event to the SEGMENT_EVENTS array`,
        );
      } else {
        setTimeout(() => {
          analytics.track(event, properties, {
            context: getAdditionalContext(),
          });
        }, delay);
      }
    });
  }
};

/**
 * Track page view and properties with Segment Analytics page method.
 */
trackingService.trackPage = (name, params = {}, delay = 500) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      const existingEvents = getAllPageEvents();
      if (!existingEvents.includes(name)) {
        Raven.captureMessage(
          `Unknown page event "${name}" being submitted. Please add your event to the PAGE_NAME array`,
        );
      } else {
        setTimeout(() => {
          analytics.page(
            name,
            {
              ...params,
              referrer: trackingReferrer || window.document.referrer,
            },
            { context: getAdditionalContext() },
          );
        }, delay);
      }
    });
    setTimeout(() => {
      ga('set', 'page', window.location.pathname);
      ga('send', 'pageview');
    }, delay);
  }
};

/* === Calls to the Let's Do This Server === */

/**
 * Resolve data event type/slug to Tracking API
 *
 * (visible-for-testing)
 */
export const getURLforDataEvent = (eventType) => {
  if (!eventType) {
    throw new ReferenceError(
      `Tracking event data "${eventType}" is not supported`,
    );
  }
  return `${TRACKING_ROOT}${eventType}`;
};

const getBeaconData = () => {
  const timestamp = performance.now();
  return {
    browserContext: getClientContext(),
    timestamp,
    anonymousId: analytics?.user?.().anonymousId?.(),
  };
};

export const unloadHandler = () => {
  if (!navigator.sendBeacon) return;
  const beaconData = JSON.stringify(getBeaconData());
  const data = new Blob([beaconData], {
    type: 'application/json; charset=UTF-8',
  });
  navigator.sendBeacon('/track/beacon', data);
};
/**
 * Track event and properties with tracking service.
 *
 * Sent to Let's Do This Server, which will load additional details about the user event
 * that took place and log to Segment there with these details.
 */
trackingService.trackDataEvent = (eventType, data) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      postJSON(
        getURLforDataEvent(eventType),
        {},
        {
          data,
          // Pass all browser context from the client up to the server.
          browserContext: getClientContext(),
          anonymousId: analytics?.user?.().anonymousId?.(),
        },
      );
    });
  }
};
export default trackingService;
